<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :tabs="tabs"
    :title="$t('model.order')"
    :onFormSubmit="handleSubmit"
    :batch-actions="batchActions"
    :onFetchData="onFetchData"
    ref="sourcePage"
    resource="orders"
  >
    <template slot="action">
      <bean-import-button
        can='orders.create'
        buttonText="导入"
        tooltipText="下载导入模板"
        url="/orders/import"
        loadingToast="导入中...请不要刷新页面"
        excelTemplateDownloadLink="/admin_api/v1/orders/import_template"
      />
      <bean-import-button
        buttonText="导入生产报价"
        :fileChangeHandler="handleImportCostPriceFileChange"
        v-if="$root.user.current_role.role.kind === 'application'"
      />
    </template>
    <template #after-table="{ value }">
      <div class="summary-info">
        <p>总数量：{{getTotalQuantity(value)}}</p>
        <p>总裁床数：{{getTotalCuttingQuantity(value)}}</p>
        <p>总实际出货量：{{getTotalCompleteQuantity(value)}}</p>
        <p>下单款数：{{ getOrderProductCount(value) }}</p>
        <p>总订单数量：{{ getOrderCount(value) }}</p>
        <p v-if="$root.user.abilities.read_product_cost_price">总金额成本：{{ getTotalCostPrice(value) }}</p>
      </div>
    </template>
  </bean-source-page>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '@/admin_grapes/orders.js';
import { columnsExtend as lineItemColumnsExtend } from '@/admin_entities/line_item_detail.js';
import { formColumnsExtend as cuttingRecordFormColumnsExtend, columnsExtend as cuttingRecordColumnsExtend } from '@/admin_entities/cutting_record_detail.js';
import { formColumnsExtend as cuttingItemFormColumnsExtend, columnsExtend as cuttingItemColumnsExtend } from '@/admin_entities/cutting_item_detail.js';
import { formColumnsExtend as deliveryFormColumnsExtend, columnsExtend as deliveryColumnsExtend } from '@/admin_entities/delivery_detail.js';
import { formColumnsExtend as deliveryItemFormColumnsExtend, columnsExtend as deliveryItemColumnsExtend } from '@/admin_entities/delivery_item_detail.js';
import { columnsExtend as orderAccessoryDeliveryColumnsExtend } from '@/admin_entities/order_accessory_delivery_detail.js';
import { columnsExtend as productColumnsExtend } from '@/admin_entities/product_detail.js';
import _ from 'lodash';
import RejectFormDialog from './reject.vue';
import { abilityService } from 'beans-admin-plugin';
import ManufactureEditModal from './manufacture-edit-modal.vue';
import PurchaseOrderEditModal from './purchase-order-edit-modal.vue';
import { getLabel } from '@/custom_form_columns_extend';
import { itemLabel, getUserLink } from '@/custom_columns_extend';
import moment from 'moment';
import UpdateAccessoryModal from './update-accessory-modal';
import LineItemAccessories from './line-item-accessories';
import { PURCHASE_ORDER_STATUS, FILE_ACCEPT, FILE_TYPE } from '@/constants';
import JSZip from 'jszip';
import ReceiveAccessoryDelivery from './receive-accessory-delivery';

@Component({
  components: {
    ManufactureEditModal,
    UpdateAccessoryModal,
    ReceiveAccessoryDelivery,
  }
})
export default class Orders extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {
    state: 'init',
    line_item_table_attributes: []
  }
  salesAndInventoryData = {}
  record = {}

  get tabs() {
    return [
      {
        label: this.$t('model.order'),
        columns: this.columns(this.$createElement, { type: 'show' })
      },
      {
        label: this.$t('model.cuttingRecord'),
        renderCell: (h, value) => {
          const orderCuttingRecords = _.get(value, 'cutting_records', []);
          const columns = cuttingRecordColumnsExtend([
            {
              prop: 'cutting_items',
              label: this.$t('cuttingRecord.cuttingItems'),
              type: 'expand',
              width: 100,
              renderCell(h, { props: { value } }) {
                const [sizeOptionValue, colorOptionValue] = lineItemColumnsExtend([
                  'size_option_value',
                  'color_option_value',
                ])

                const CuttingItemsColumns = cuttingItemColumnsExtend([
                  { ...colorOptionValue, prop: 'line_item.color_option_value' },
                  { ...sizeOptionValue, prop: 'line_item.size_option_value' },
                  'quantity',
                ])

                return <bean-table style={{ width: '800px' }} value={value} columns={CuttingItemsColumns} />;
              }
            },
            'cutting_at',
          ])

          const actions = ({ row }) => {
            return [
              {
                component: 'dialogFormButton',
                props: {
                  label: '编辑',
                  can: 'cutting_records.update',
                  columns: cuttingRecordFormColumnsExtend([
                    {
                      key: 'cutting_at',
                      renderCell: {
                        required: true,
                        props: {
                          disabled: true
                        }
                      }
                    },
                    {
                      key: 'cutting_items',
                      renderCell: {
                        required: true,
                        component: 'nestForm',
                        props: {
                          limitOne: true,
                          columns: cuttingItemFormColumnsExtend([
                            {
                              prop: 'line_item.color_option_value.name',
                              label: this.$t('lineItem.colorOptionValue'),
                              renderCell: {
                                component: 'input',
                                props: {
                                  disabled: true
                                }
                              }
                            },
                            {
                              prop: 'line_item.size_option_value.name',
                              label: this.$t('lineItem.sizeOptionValue'),
                              renderCell: {
                                component: 'input',
                                props: {
                                  disabled: true
                                }
                              }
                            },
                            'quantity'
                          ], { data: row.cutting_items, type: 'edit' })
                        }
                      }
                    }
                  ], { data: row, type: 'edit' }),
                  value: row,
                  submitHandler: async (value) =>  {
                    await this.$autoLoading(this.$request.put(`/cutting_records/${row.id}`, value));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              }
            ].filter(Boolean)
          }

          return <bean-table
            value={orderCuttingRecords}
            columns={columns}
            actions={actions}
          />
        }
      },
      {
        label: this.$t('order.deliveries'),
        renderCell: (h, value) => {
          const order = value;
          const orderDeliveries = _.get(value, 'deliveries', []);
          const columns = deliveryColumnsExtend([
            {
              prop: 'delivery_items',
              label: this.$t('delivery.deliveryItems'),
              type: 'expand',
              width: 80,
              renderCell(h, { props: { value } }) {
                const [sizeOptionValue, colorOptionValue] = lineItemColumnsExtend([
                  'size_option_value',
                  'color_option_value',
                ])

                const DeliveryItemsColumns = deliveryItemColumnsExtend([
                  { ...colorOptionValue, prop: 'line_item.color_option_value' },
                  { ...sizeOptionValue, prop: 'line_item.size_option_value' },
                  'quantity',
                ])

                return <bean-table style={{ width: '800px' }} value={value} columns={DeliveryItemsColumns} />;
              }
            },
            {
              prop: 'delivery_at',
              label: '时间',
              minWidth: 160,
              renderCell: (h, { props: { scope: { row } } }) => {
                const timeFormat = 'YYYY-MM-DD HH:mm:ss'
                const dateFormat = 'YYYY-MM-DD'

                return (
                  <div>
                    <div>{this.$t('delivery.deliveryAt')}: {moment(row.delivery_at).format(timeFormat)}</div>
                    {row.reach_on ? <div>{this.$t('delivery.reachOn')}: {moment(row.reach_on).format(dateFormat)}</div> : null}
                    {row.check_on ? <div>{this.$t('delivery.checkOn')}: {moment(row.check_on).format(dateFormat)}</div> : null}
                  </div>
                )
              }
            },
            'standard_delivery_form',
            'broken_outer_package',
            'has_original',
            'sampling_rate',
            'delivery_quantity',
            'quality_comment',
            'check_images',
            'state',
            'defective_quantity',
            'remark'
          ])

          const actions = ({ row }) => {
            const abilities = _.get(row, 'abilities', {});

            return [
              abilities.update && {
                component: 'dialogFormButton',
                props: {
                  label: '编辑',
                  columns: deliveryFormColumnsExtend([
                    {
                      key: 'delivery_at',
                      renderCell: {
                        required: true,
                        props: {
                          disabled: true
                        }
                      }
                    },
                    abilities.edit_defective_info && 'defective_quantity',
                    abilities.edit_defective_info && 'remark',
                    abilities.edit_columns && {
                      key: 'delivery_items_attributes',
                      renderCell: {
                        required: true,
                        component: 'nestForm',
                        props: {
                          limitOne: true,
                          columns: deliveryItemFormColumnsExtend([
                            {
                              prop: 'line_item.color_option_value.name',
                              label: this.$t('lineItem.colorOptionValue'),
                              renderCell: {
                                component: 'input',
                                props: {
                                  disabled: true
                                }
                              }
                            },
                            {
                              prop: 'line_item.size_option_value.name',
                              label: this.$t('lineItem.sizeOptionValue'),
                              renderCell: {
                                component: 'input',
                                props: {
                                  disabled: true
                                }
                              }
                            },
                            'quantity'
                          ], { data: row.delivery_items_attributes, type: 'edit' })
                        }
                      }
                    }
                  ], { data: row, type: 'edit' }),
                  value: row,
                  submitHandler: async (value) =>  {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}`, value));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.print && {
                component: 'confirmButton',
                props: {
                  label: '打印出货单',
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    const res = await this.$autoLoading(this.$request.get(`/deliveries/${row.id}/print`, null, { responseType: 'arraybuffer' }));
                    const blob = new Blob([res], { type: 'application/pdf' });
                    const pdfUrl = URL.createObjectURL(blob);
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = pdfUrl;
                    const onFocus = () => {
                      iframe.onload = null;
                      iframe.remove();
                      URL.revokeObjectURL(pdfUrl);
                      window.removeEventListener('focus', onFocus)
                    }
                    window.addEventListener('focus', onFocus)
                    iframe.onload = () => {
                      iframe.contentWindow.print();
                    };
                    document.body.appendChild(iframe);
                  }
                }
              },
              abilities.reach && {
                component: 'confirmButton',
                props: {
                  label: '到仓',
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/reach`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.deliver && {
                component: 'confirmButton',
                props: {
                  label: '发货',
                  type: 'primary'
                },
                on: {
                  confirm: async () =>  {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/deliver`, value));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              (abilities.check || abilities.fail) && {
                component: 'dialogFormButton',
                props: {
                  label: '质检',
                  columns: deliveryFormColumnsExtend([
                    {
                      label: '供应商等级',
                      prop: 'company_rating',
                      renderCell: () => {
                        return <span>{order.company.current_month_rating}</span>
                      }
                    },
                    { key: 'standard_delivery_form' },
                    { key: 'broken_outer_package' },
                    {
                      label: '是否合格',
                      prop: 'qualified',
                      renderCell: {
                        component: 'switch',
                      }
                    },
                    { key: 'has_original' },
                    { key: 'sampling_rate', renderCell: { required: true } },
                    { key: 'delivery_quantity', renderCell: { required: true } },
                    { key: 'reach_on', renderCell: { required: true } },
                    { key: 'check_on', renderCell: { required: true } },
                    { key: 'quality_comment', renderCell: { required: true } },
                    { key: 'check_images' },
                  ], { data: row, type: 'edit' }),
                  value: {
                    standard_delivery_form: false,
                    broken_outer_package: false,
                    has_original: false,
                    qualified: false,
                    delivery_quantity: null,
                    sampling_rate: null,
                    quality_comment: null,
                    check_on: new Date(),
                    reach_on: null,
                  },
                  submitHandler: async (value) =>  {
                    const action = value.qualified ? 'check' : 'fail';

                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/${action}`, value));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.update_check && {
                component: 'dialogFormButton',
                props: {
                  label: '编辑质检信息',
                  columns: deliveryFormColumnsExtend([
                    row.abilities.edit_check_columns && { key: 'standard_delivery_form' },
                    row.abilities.edit_check_columns && { key: 'broken_outer_package' },
                    row.abilities.edit_check_columns && { key: 'has_original' },
                    row.abilities.edit_check_columns && { key: 'sampling_rate', renderCell: { required: true } },
                    row.abilities.edit_check_columns && { key: 'delivery_quantity', renderCell: { required: true } },
                    row.abilities.edit_check_columns && { key: 'reach_on', renderCell: { required: true } },
                    row.abilities.edit_check_columns && { key: 'check_on', renderCell: { required: true } },
                    row.abilities.edit_check_columns && { key: 'quality_comment', renderCell: { required: true } },
                    row.abilities.edit_check_images && { key: 'check_images' },
                  ].filter(Boolean), { data: row, type: 'edit' }),
                  value: row,
                  submitHandler: async (value) =>  {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/update_check`, value));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.inbound && {
                component: 'confirmButton',
                props: {
                  label: '入库',
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/inbound`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.mark_as_checked && {
                component: 'confirmButton',
                props: {
                  label: '合格',
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/mark_as_checked`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
              abilities.mark_as_failed && {
                component: 'confirmButton',
                props: {
                  label: '不合格',
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.put(`/deliveries/${row.id}/mark_as_failed`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              }
            ].filter(Boolean)
          }

          return <bean-table
            value={orderDeliveries}
            columns={columns}
            actions={actions}
          />
        }
      },
      (_.get(this.record, 'accessory_state') === 'need' || _.get(this.record, 'order_accessory_deliveries', []).length) && {
        label: '辅料',
        renderCell: (h, value) => {
          const order = value;
          const orderAccessoryDeliveries = _.get(value, 'order_accessory_deliveries', []);
          const columns = orderAccessoryDeliveryColumnsExtend([
            {
              prop: 'order_accessory_delivery_items',
              label: this.$t('orderAccessoryDelivery.orderAccessoryDeliveryItems'),
              type: 'expand',
              width: 80,
              renderCell(h, { props: { scope: { row } } }) {
                let lineItems = _.cloneDeep(order.line_items)

                _.each(lineItems, (lineItem) => {
                  lineItem.line_item_accessories = [];

                  _.each(row.accessories, (accessory) => {
                    const orderAccessoryDeliveryItem = _.find(row.order_accessory_delivery_items, { accessory_id: accessory.id, line_item_id: lineItem.id });

                    lineItem.line_item_accessories.push({
                      quantity: orderAccessoryDeliveryItem.quantity
                    })
                  })
                })

                return (
                  <div>
                    <LineItemAccessories accessories={row.accessories} lineItems={lineItems}  />
                  </div>
                )
              }
            },
            'number',
            'state',
          ])

          const actions = ({ row }) => {
            const abilities = _.get(row, 'abilities', {});
            const confirmButtonConfig = (title, event) => {
              return {
                component: 'confirmButton',
                props: {
                  label: title,
                  type: 'primary'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.put(`/order_accessory_deliveries/${row.id}/${event}`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              }
            }

            return [
              abilities.update ? () => {
                const submitHandler = async (value) => {
                  await this.handleUpdateAccessory(row.id, value);
                  this.$refs.sourcePage.fetchData();
                }
                return <UpdateAccessoryModal line-items={order.line_items} orderAccessoryDelivery={row} submitHandler={submitHandler} />
              } : null,
              abilities.submit && confirmButtonConfig('提交辅料', 'submit'),
              abilities.withdraw && confirmButtonConfig('撤回提交辅料', 'withdraw'),
              abilities.confirm && confirmButtonConfig('确认接收辅料', 'confirm'),
              abilities.receive ? () => <ReceiveAccessoryDelivery onSuccess={() => this.$refs.sourcePage.fetchData()} orderAccessoryDeliveryId={row.id} accessories={row.accessories} /> : null,
              abilities.reject && (
                () => <RejectFormDialog data={row} title='拒收辅料' rejectReasons={[]} submitHandler={async (value) => {
                  await this.$autoLoading(this.$request.put(`/order_accessory_deliveries/${row.id}/reject`, { reject_reason: value.reject_reason }));
                  this.$message.success(this.$t('operateSuccess'));
                  this.$refs.sourcePage.fetchData();
                }} />
              ),
              abilities.destroy && {
                component: 'confirmButton',
                props: {
                  label: '删除',
                  type: 'danger'
                },
                on: {
                  confirm: async () => {
                    await this.$autoLoading(this.$request.delete(`/order_accessory_deliveries/${row.id}`));
                    this.$message.success(this.$t('operateSuccess'));
                    this.$refs.sourcePage.fetchData();
                  }
                }
              },
            ].filter(Boolean)
          }

          return (
            <div>
              <bean-import-button
                buttonText="导入辅料"
                tooltipText="下载导入模板"
                url={`/orders/${order.id}/order_accessory_deliveries/import`}
                loadingToast="导入中...请不要刷新页面"
                // excelTemplateDownloadLink="/admin_api/v1/order_accessory_deliveries/import_template"
                fileChangeHandler={(e) => this.handleImprtAccessoryFileChange(e, this.record.id)}
              />
              <bean-table
                style={{ marginTop: '10px' }}
                actionColumnProps={{ fixed: undefined }}
                value={orderAccessoryDeliveries}
                columns={columns}
                height={null}
                actions={actions}
              />
            </div>
          )
        }
      },
      {
        label: this.$t('model.operationLog'),
        renderCell: (h, value) => {
          const operationLogsTable = (name, operationLogs) => {
            return <el-table style={{ marginBottom: '20px' }} data={operationLogs}>
              <el-table-column label={name}>
                <el-table-column
                  prop="created_at"
                  label="操作时间"
                  scopedSlots={{
                    default: (scope) => {
                      value = operationLogs[scope.$index].created_at

                      return moment(value).format('YYYY-MM-DD HH:mm:ss')
                    }
                  }}
                />
                <el-table-column
                  prop="operator"
                  label="操作人"
                  scopedSlots={{
                    default: (scope) => {
                      value = operationLogs[scope.$index].operator

                      if (value) {
                        return <bean-link keepNode to={{ name: 'users.show', params: { id: value.id } }}>{value.profile.name}</bean-link>
                      } else {
                        return <span>系统</span>
                      }
                    }
                  }}
                />
                <el-table-column prop="event_description" label="事件" />
                <el-table-column
                  prop="detail"
                  label="详情"
                  scopedSlots={{
                    default: (scope) => {
                      const operationLog = operationLogs[scope.$index]

                      if (operationLog.event === 'update_order_details') {
                        const data = operationLog.data || {}
                        const filesData = data.files || {}

                        const renderFiles = () => {
                          const fileElements = []

                          if (filesData.added && filesData.added.length > 0) {
                            fileElements.push(<div>新增附件：</div>)
                            filesData.added.forEach((file, index) => {
                              if (file.url) {
                                fileElements.push(<div key={`added-${index}`}><el-link href={file.url} target="_blank">{file.filename}</el-link></div>)
                              }
                            })
                          }
                          if (filesData.deleted && filesData.deleted.length > 0) {
                            fileElements.push(<div>删除附件：</div>)
                            filesData.deleted.forEach((file, index) => {
                              if (file.url) {
                                fileElements.push(<div key={`deleted-${index}`}><el-link href={file.url} target="_blank">{file.filename}</el-link></div>)
                              }
                            })
                          }
                          return fileElements.length > 0 ? <div>{fileElements}</div> : null
                        }
                        return renderFiles()
                      }
                      value = operationLog.detail
                      return <span style="white-space: pre-wrap; word-break: break-all">{value}</span>
                    }
                  }}
                />
                <el-table-column
                  prop="time_consuming"
                  label="耗时（秒）"
                  scopedSlots={{
                    default: (scope) => {
                      value = operationLogs[scope.$index].time_consuming

                      if (value) {
                        return <span>{value}</span>
                      } else {
                        return <span>-</span>
                      }
                    }
                  }}
                />
                <el-table-column
                  prop="time_consuming_hour"
                  label="耗时（小时）"
                  scopedSlots={{
                    default: (scope) => {
                      value = operationLogs[scope.$index].time_consuming

                      if (value) {
                        return <span>{(value / 3600).toFixed(2)}</span>
                      } else {
                        return <span>-</span>
                      }
                    }
                  }}
                />
              </el-table-column>
            </el-table>
          }

          return (
            <div class="operation-logs">
              {operationLogsTable('订单', _.get(value, 'state_operation_logs', []))}
              {
                _.map(_.get(value, 'deliveries', []), (delivery) => {
                  return operationLogsTable(`出货单 - ${moment(delivery.delivery_at).format('YYYY-MM-DD HH:mm:ss')}`, delivery.operation_logs)
                })
              }
              {
                _.map(_.get(value, 'order_accessory_deliveries', []), (orderAccessoryDelivery) => {
                  return operationLogsTable(`辅料 - #${orderAccessoryDelivery.number}`, orderAccessoryDelivery.operation_logs)
                })
              }
            </div>
          )
        }
      }
    ].filter(Boolean)
  }
  salesDirectorRejectReasons = [];
  merchandiserRejectReasons = [];
  adminRejectReasons = [];
  bossRejectReasons = [];
  supplierBossRejectReasons = [];

  async fetchProductSalesAndInventory(productId) {
    this.salesAndInventoryData = await this.$request.get(`/products/${productId}/sales_and_inventory`)
  }

  async onFetchData({ type, resource }) {
    if (type === 'index') {
      const { data, pagination } = await this.$request.get(`/${resource}`, { params: { ...this.$route.query } });
      return {
        data,
        pagination
      }
    }

    if (type === 'show' || type === 'edit') {
      const data = await this.$request.get(`/${resource}/${this.$route.params.id}`);

      if (abilityService.can('products.read_sales_and_inventory')) {
        await this.fetchProductSalesAndInventory(data.product_id)
      }

      if (type === 'show') {
        this.record = data
      }

      if (type === 'edit') {
        data.line_item_table_attributes = this.setLineItemTableAttributes({ order: data })
      }

      return { data };
    }

    if (type === 'new') {
      return { data: { state: 'init' } }
    }
  }

  get displayInternalRemark() {
    return ['salesman', 'sales_director', 'merchandiser', 'application', 'quality_inspector'].includes(this.roleKind);
  }

  usersSelectProps({ item, query }) {
    const v = { extra: { model_name: 'User' } }
    let hash = {
      xRemotePreload: async () => {
        const { data } = await this.$request.get('users/read_options', { per_page: 100, is_block_eq: false, ...query });
        return data.map(object => ({ value: object.id, label: getLabel({ item: object, v: v }) }));
      },
      xRemoteSearch: async (keyword) => {
        const { data } = await this.$request.get('users/read_options', { keyword, ...query });
        return data.map(object => ({ value: object.id, label: getLabel({ item: object, v: v }) }));
      }
    }

    if (item) {
      hash.options = [{ value: item['id'], label: getLabel({ item, v: v }) }]
    }

    return hash
  }

  deliveryOnRenderCell({ type, data }) {
    return {
      props: {
        pickerOptions: {
          disabledDate(time) {
            let dateTime

            if (type === 'new') {
              dateTime = moment(Date.now()).startOf('day')
            } else {
              dateTime = moment(data.created_at).startOf('day')
            }

            return time.getTime() < dateTime
          }
        }
      }
    }
  }

  getTotalQuantity(value) {
    return _.sumBy(value, (order) => { return _.sumBy(order.line_items, (item) => item.quantity) })
  }

  getTotalCuttingQuantity(value) {
    return _.sumBy(value, (order) => { return _.sumBy(order.line_items, (item) => item.cutting_quantity) })
  }

  getTotalCompleteQuantity(value) {
    return _.sumBy(value, (order) => { return _.sumBy(order.line_items, (item) => item.complete_quantity) })
  }

  getOrderProductCount(value) {
    return _.uniqBy(value, 'product_id').length
  }

  getOrderCount(value) {
    return value.length
  }

  getTotalCostPrice(value) {
    return _.sumBy(value, (order) => { return _.sumBy(order.line_items, (item) => order.product.cost_price * item.quantity) })
  }

  async mounted() {
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    if (['application', 'merchandiser'].includes(roleKind)) {
      const data = await this.$request.get('/reject_reasons/read_options', { kind_eq: 'merchandiser_reject_order', page: 0 });

      this.merchandiserRejectReasons = data
    }

    if (['application', 'sales_director'].includes(roleKind)) {
      const data = await this.$request.get('/reject_reasons/read_options', { kind_eq: 'sales_director_reject_order', page: 0 });

      this.salesDirectorRejectReasons = data
    }

    if (['supplier_boss', 'supplier'].includes(roleKind)) {
      this.supplierBossRejectReasons = (await this.$request.get('/reject_reasons/read_options', { kind_eq: 'supplier_reject_order', page: 0 }));
    }

    if (['application'].includes(roleKind)) {
      const data = await this.$request.get('/reject_reasons/read_options', { kind_eq: 'boss_reject_order', page: 0 });

      this.bossRejectReasons = data
    }

    if (['application'].includes(roleKind)) {
      this.adminRejectReasons = (await this.$request.get('/reject_reasons/read_options', { kind_eq: 'admin_reject_order', page: 0 }))
    }
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');
    const supplierRelatedRole = ['supplier_boss', 'supplier'].includes(roleKind)

    let states = [
      'wait_supplier_confirm',
      'confirmed',
      'fabric_arrived',
      'cutting_updated',
      'printing_in_progress',
      'workshop_in_progress',
      'tail_in_progress',
      'partial',
      'completed',
    ]

    if (roleKind === 'supplier_boss') {
      states = ['wait_supplier_boss_confirm'].concat(states)
    } else if (!supplierRelatedRole) {
      states = [
        'init',
        'wait_sales_director_audit',
        'sales_director_rejected',
        'pending',
        'rejected',
        'wait_admin_audit',
        'admin_rejected',
        'wait_boss_audit',
        'boss_rejected',
        'wait_supplier_boss_confirm',
        'supplier_boss_rejected',
        'wait_supplier_confirm',
        'supplier_rejected',
        'confirmed',
        'fabric_arrived',
        'cutting_updated',
        'printing_in_progress',
        'workshop_in_progress',
        'tail_in_progress',
        'partial',
        'completed',
      ]
    }

    const array = [
      'number_cont',
      {
        key: 'product_style_number_cont_any',
        renderCell: {
          component: 'input'
        }
      },
      {
        prop: 'overdue_state',
        label: this.$t('order.deliveryOn'),
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: [{ label: '未逾期', value: 'no_overdue' }, { label: '即将逾期', value: 'upcoming_overdue' }, { label: '已逾期', value: 'overdue' }, { label: '按时交货', value: 'on_time' }, { label: '提前交货', value: 'in_advance' }]
          }
        }
      },
      {
        prop: 'plan_completed_on_compare_delivery_on',
        label: '已变更',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: [{ label: '是', value: 'not_null_and_diff' }, { label: '否', value: 'is_null_or_same' }]
          }
        }
      },
      {
        prop: 'state_in',
        label: this.$t('order.state'),
        renderCell: {
          component: 'select',
          attrs: {
            collapseTags: true,
          },
          props: {
            clearable: true,
            multiple: true,
            options: _.map(states, (value) => ({ label: this.$t(`order.states.${value}`), value: value }))
          }
        }
      },
      {
        prop: 'purchase_order_combined_status_eq',
        label: '采购单收货状态',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: _.map(PURCHASE_ORDER_STATUS, (value, key) => ({ label: value, value: key }))
          }
        }
      },
      {
        prop: 'order_accessory_deliveries_state_eq',
        label: '辅料状态',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: _.map([
              'pending', 'submited', 'rejected', 'confirmed', 'received'
            ], (value) => ({ label: this.$t(`orderAccessoryDelivery.states.${value}`), value: value }))
          }
        }
      },
      {
        key: 'deliveries_state_eq',
        label: '发货状态',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: _.map(['delivering', 'reached', 'checked', 'inbounded', 'failed'], (value) => ({
              label: this.$t(`delivery.states.${value}`), value: value
            }))
          }
        }
      },
      {
        key: 'creator_id_eq',
        renderCell: {
          props: this.usersSelectProps({ query: { role_in: ['salesman', 'sales_director'] } })
        }
      },
      {
        prop: 'sales_directors_id_eq',
        label: '销售主管',
        renderCell: {
          component: 'select',
          props: this.usersSelectProps({ query: { role_eq: 'sales_director' } })
        }
      },
      !supplierRelatedRole && !['salesman', 'sales_director'].includes(roleKind) && {
        key: 'company_id_in',
        renderCell: {
          props: {
            multiple: true,
          }
        }
      },
      roleKind === 'supplier_boss' && {
        key: 'supplier_id_eq',
        renderCell: {
          props: this.usersSelectProps({ query: { role_in: ['supplier', 'supplier_boss'], company_id_eq: this.$root.user.user.company_id } })
        }
      },
      {
        prop: 'order_at_gteq',
        label: this.$t('order.orderAt'),
        renderCell: {
          component: 'dateTimePicker',
          props: {
            placeholder: this.$t('hint.gteq')
          }
        }
      },
      {
        prop: 'order_at_lteq',
        label: this.$t('order.orderAt'),
        renderCell: {
          component: 'dateTimePicker',
          props: {
            placeholder: this.$t('hint.lteq')
          }
        }
      },
      // abilityService.can('virtual_warehouses.read_options') && 'virtual_warehouse_id_eq',
      {
        key: 'is_push_to_jushuitan_eq',
        label: '已推送采购单',
      },
      ['salesman', 'sales_director', 'merchandiser', 'application', 'supplier_boss', 'supplier'].includes(roleKind) && {
        prop: 'warning',
        label: '预警',
        renderCell: {
          component: 'switch'
        }
      },
      ['salesman', 'sales_director'].includes(roleKind) && {
        prop: 'todo',
        label: '待处理',
        renderCell: {
          component: 'switch'
        }
      }
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  findSkuSalesAndInventoryBySkuAndColorAndSize({ sku, color, size }) {
    const splitedColor = _.map(color.split('#'), (element) => element.trim())

    return _.find(this.salesAndInventoryData.skus, (element) => element.sku_id === sku ||
      element.properties_value === `${color};${size}`) ||
      _.find(this.salesAndInventoryData.skus, (element) => element.properties_value === `${splitedColor[0]};${size}`) ||
      _.find(this.salesAndInventoryData.skus, (element) => element.properties_value === `${splitedColor[1]};${size}`)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const abilities = _.get(this.$root.user, 'abilities');
    const [productImage, productNumber] = productColumnsExtend(['image', 'number'])

    if (type === 'index') {
      const array = [
        'order_at',
        { ...productImage, prop: 'product.image' },
        {
          prop: 'delivery_on',
          label: '订单信息',
          sortable: true,
          minWidth: 220,
          renderCell: (h, { props: { scope: { row } } }) => {
            return (
              <div style="flex: 1; min-width: 0; text-align: left;">
                <div style="display: flex; flex-direction: column; gap: 4px;">
                  <div><strong>{this.$t('order.number')}</strong> {row.number}</div>
                  <div><strong>{this.$t('product.number')}</strong> {row.product.number}</div>
                  <div>
                    <strong>{this.$t('product.styleNumber')} </strong>
                    <bean-link keepNode to={{ name: 'products.show', params: { id: row.product.id } }}>{itemLabel(row.product)}</bean-link>
                    {row.is_pre_order ?
                      <el-tag size="mini" type="warning" style="margin-left: 8px;">预下单</el-tag> :
                      <el-tag size="mini" type="success" style="margin-left: 8px;">下单</el-tag>
                    }
                  </div>
                  <div><strong>{this.$t('order.quantity')}</strong> {row.quantity}件</div>
                  <div><strong>{this.$t('order.deliveryOn')} </strong>
                    {moment(row.delivery_on).format('YYYY-MM-DD')}
                    {row.plan_completed_on && ` (${this.$t('order.planCompletedOn')}: ${moment(row.plan_completed_on).format('YYYY-MM-DD')})`}
                    [{row.overdue_state}]
                  </div>
                  {row.requested_delivery_on && <div><strong>申请交货日期</strong> {moment(row.requested_delivery_on).format('YYYY-MM-DD')}</div>}
                  {row.delivery_change_request_state && row.delivery_change_request_state !== 'init' && <div><strong>货期修改申请状态</strong> {this.$t(`order.deliveryChangeRequestStates.${row.delivery_change_request_state}`)}</div>}
                  <div><strong>{this.$t('order.creator')}</strong> {getUserLink(h, { item: row.creator })}</div>
                  {row.supplier && <div><strong>{this.$t('order.supplier')}</strong> {getUserLink(h, { item: row.supplier })}</div>}
                  {row.purchase_order && <div><strong>采购单收货状态</strong> {PURCHASE_ORDER_STATUS[row.purchase_order.combined_status]}</div>}
                  {row.remark && <div><strong>{this.$t('order.remark')}</strong> {row.remark}</div>}
                  {row.supplier_remark && <div><strong>{this.$t('order.supplierRemark')}</strong> {row.supplier_remark}</div>}
                  {row.internal_remark && this.displayInternalRemark && <div><strong>{this.$t('order.internalRemark')}</strong> {row.internal_remark}</div>}
                </div>
              </div>
            )
          }
        },
        {
          prop: 'line_items',
          label: this.$t('order.lineItems'),
          minWidth: 250,
          renderCell: (h, { props: { value } }) => {
            // 按颜色分组数据
            const groupedByColor = {};

            // 收集所有尺寸选项并按 position 排序
            const sizeOptionsMap = new Map();

            // 初始化分组结构
            value.forEach(item => {
              const colorName = item.color_option_value.name;
              const sizeName = item.size_option_value.name;
              const sizePosition = item.size_option_value.position || 0;
              const quantity = item.quantity || 0;

              // 收集尺寸选项和位置信息
              sizeOptionsMap.set(sizeName, sizePosition);

              if (!groupedByColor[colorName]) {
                groupedByColor[colorName] = {
                  color: colorName,
                  sizes: {},
                  total: 0
                };
              }

              groupedByColor[colorName].sizes[sizeName] = quantity;
              groupedByColor[colorName].total += quantity;
            });

            // 按 position 排序尺寸选项
            const sizeOptions = Array.from(sizeOptionsMap.entries())
              .sort((a, b) => a[1] - b[1])
              .map(([size]) => size);

            // 转换为表格数据
            const tableData = Object.values(groupedByColor).map(group => {
              const row = {
                color: group.color,
                total: group.total
              };

              sizeOptions.forEach(size => {
                row[size] = group.sizes[size] || 0;
              });

              return row;
            });

            const columns = [
              {
                key: 'color',
                label: '颜色',
                minWidth: 80,
                renderCell: (h, { props: { scope: { row } } }) => {
                  return <span>{row.color}</span>;
                }
              },
              ...sizeOptions.map(size => ({
                key: size,
                label: size,
                minWidth: 60,
                renderCell: (h, { props: { scope: { row } } }) => {
                  return <span>{row[size]}</span>;
                }
              })),
              {
                key: 'total',
                label: '合计',
                minWidth: 60,
                renderCell: (h, { props: { scope: { row } } }) => {
                  return <span>{row.total}</span>;
                }
              }
            ];

            return <bean-table
              value={tableData}
              columns={columns}
              actions={null}
              height={null}
            />;
          }
        },
        'state',
      ]

      return columnsExtend(array)
    }

    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    let displayColumns = []
    let additionalDisplayColumns = []

    if (roleKind === 'application') {
      additionalDisplayColumns = ['liquidated_damages']
    } else if (roleKind === 'supplier_boss') {
      additionalDisplayColumns = ['liquidated_damages']
    }

    displayColumns = displayColumns.concat(additionalDisplayColumns)

    const array = [
      type === 'show' && 'id',
      {
        key: 'order_at',
        sortable: true
      },
      { key: 'number', minWidth: 110 },
      type === 'show' && { ...productNumber, prop: 'product.number' },
      'product',
      type === 'show' && { ...productImage, prop: 'product.image' },
      type === 'show' && abilityService.can('products.read_sales_and_inventory') && {
        prop: 'sales_and_inventory',
        label: '销量与库存',
        renderCell: () => this.salesAndInventoryRenderCell()
      },
      {
        prop: 'line_items',
        label: this.$t('order.lineItems'),
        type: 'expand',
        width: 80,
        renderCell: (h, { props: { value } }) => {
          const displaySkuSalesAndInventory = type === 'show' && abilityService.can('products.read_sales_and_inventory') && ![null, undefined].includes(this.salesAndInventoryData.recent_7_days_sales_count)

          _.each(value, (lineItem) => {
            const salesAndInventoryData = this.findSkuSalesAndInventoryBySkuAndColorAndSize({ sku: lineItem.sku, color: lineItem.color_option_value.name, size: lineItem.size_option_value.name })

            if (salesAndInventoryData) {
              lineItem.inventory_real_available_qty = salesAndInventoryData.inventory_real_available_qty
              lineItem.recent_15_days_sales_count  = salesAndInventoryData.recent_15_days_sales_count
              lineItem.recent_30_days_sales_count  = salesAndInventoryData.recent_30_days_sales_count
              lineItem.inventory_purchase_qty = salesAndInventoryData.inventory_purchase_qty
              lineItem.labels = salesAndInventoryData.labels
            } else {
              lineItem.inventory_real_available_qty = null
              lineItem.recent_15_days_sales_count = null
              lineItem.recent_30_days_sales_count = null
              lineItem.inventory_purchase_qty = null
              lineItem.labels = []
            }
          })

          const lineItemsColumns = lineItemColumnsExtend([
            displaySkuSalesAndInventory && {
              prop: 'labels',
              label: '标签',
              minWidth: 120,
              renderCell: (h, { props: { value } }) => {
                if (value && value.length) {
                  return _.map(value, (object) => <el-tag style={{ 'margin-right': '3px' }}>{object}</el-tag>)
                }
              }
            },
            'color_option_value',
            'size_option_value',
            'quantity',
            'cutting_quantity',
            'complete_quantity',
            displaySkuSalesAndInventory && {
              prop: 'inventory_real_available_qty',
              label: '库存',
            },
            displaySkuSalesAndInventory && {
              prop: 'recent_15_days_sales_count',
              label: '近15天销量',
            },
            displaySkuSalesAndInventory && {
              prop: 'recent_30_days_sales_count',
              label: '近30天销量',
            },
            displaySkuSalesAndInventory && {
              prop: 'inventory_purchase_qty',
              label: '采购在途数',
            }
          ].filter(Boolean))

          const total = value.reduce((accumulator, item) => {
            ['quantity', 'cutting_quantity', 'complete_quantity'].forEach(key => {
              accumulator[key] = (accumulator[key] || 0) + item[key];
            })

            if (displaySkuSalesAndInventory) {
              ['inventory_real_available_qty', 'recent_15_days_sales_count', 'recent_30_days_sales_count', 'inventory_purchase_qty'].forEach(key => {
                accumulator[key] = (accumulator[key] || 0) + item[key];
              })
            }

            return accumulator;
          }, {})
          total['color_option_value'] = { name: '总数' }
          total['size_option_value'] = { name: '：' }

          const valueWithTotal = [...value, total]
          const width = type === 'show' ? '100%' : '800px'

          return <bean-table style={{ width: width }} value={valueWithTotal} columns={lineItemsColumns} height={null} actions={[]} />;
        }
      },
      type === 'show' && 'is_pre_order',
      type === 'show' && 'accessory_reach_on',
      {
        key: 'delivery_on',
        sortable: true,
        minWidth: 170,
        renderCell(h, { props: { value, scope: { row } } }) {
          return (
            <div>
              <div>{ value }</div>
              {
                (() => {
                  if (row.plan_completed_on) {
                    return <div>计划完成：{row.plan_completed_on}</div>
                  }
                })()
              }
              <div>{ row.overdue_state }</div>
            </div>
          )
        }
      },
      type === 'show' && {
        key: 'quality_requirement',
      },
      type === 'show' && {
        key: 'customer_level',
      },
      { key: 'state', minWidth: 150 },
      {
        prop: 'purchase_order.combined_status',
        label: '采购单收货状态',
        renderCell(h, { props: { value } }) {
          if (value) {
            return <el-tag type='primary'>{PURCHASE_ORDER_STATUS[value]}</el-tag>
          }
        }
      },
      {
        key: 'quantity',
        sortable: true,
      },
      'remark',
      this.displayInternalRemark && 'internal_remark',
      'creator',
      (type === 'show' || ['salesman', 'sales_director'].includes(roleKind)) && 'merchandiser',
      !['salesman', 'sales_director'].includes(roleKind) && 'company',
      'factory',
      (type === 'show' || roleKind === 'supplier_boss') && 'supplier',
      'supplier_remark',
      'factory_remark',
      // 'virtual_warehouse',
      type === 'show' && _.get(this.record.abilities, 'read_cost') && {
        key: 'cost_price',
        renderCell: (h, { props: { value, scope: { row } } }) => {
          return (
            <el-collapse>
              <el-collapse-item title={value}>
                <div>
                  <div>{this.$t('order.fabricCost')}: {row.fabric_cost}</div>
                  <div>{this.$t('order.assistCost')}: {row.assist_cost}</div>
                  <div>{this.$t('order.craftCost')}: {row.craft_cost}</div>
                  <div>{this.$t('order.processingCost')}: {row.processing_cost}</div>
                  <div>{this.$t('order.managementCost')}: {row.management_cost}</div>
                </div>
              </el-collapse-item>
            </el-collapse>
          )
        }
      },
      type === 'show' && abilities.read_product_cost_price && 'total_product_cost',
      abilities.read_watch && 'watch',
      type === 'show' && 'audited_at',
      type === 'show' && 'confirmed_at',
      type === 'show' && 'receive_cloth_on',
      type === 'show' && 'receive_cloth_remark',
      type === 'show' && 'order_cutting_dates',
      type === 'show' && 'order_cutting_remark',
      type === 'show' && 'printing_start_on',
      type === 'show' && 'printing_remark',
      type === 'show' && 'workshop_start_on',
      type === 'show' && 'workshop_remark',
      type === 'show' && 'tail_start_on',
      type === 'show' && 'tail_remark',
      type === 'show' && 'merchandiser_inspection_remark',
      type === 'show' && 'merchandiser_inspection_remark_images',
      type === 'show' && 'delivery_remark',
      type === 'show' && 'completed_at',
      type === 'show' && 'plan_completed_on',
      type === 'show' && 'reject_reason',
      type === 'show' && 'supplier_first_reject_reason',
      type === 'show' && 'supplier_boss_first_reject_reason',
      type === 'show' && 'overdue_state',
      type === 'show' && 'overdue_reason',
      displayColumns.includes('liquidated_damages') && 'liquidated_damages',
      type === 'show' && 'requested_delivery_on',
      type === 'show' && 'delivery_change_request_state',
      type === 'show' && 'delivery_change_request_reject_reason',
      type === 'show' && 'delivery_change_request_remarks',
      type === 'show' && 'pre_production_meeting_minutes',
      type === 'show' && 'pre_production_meeting_images',
      type === 'show' && 'files',
      type === 'show' && 'created_at',
      type === 'show' && 'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  productIdRenderCell({ type, change, data }) {
    return {
      hint: '选择后会清空订单项，且创建订单后不可再修改',
      props: {
        clearable: false,
        disabled: type === 'edit',
        xRemotePreload: async () => {
          const { data } = await this.$request.get('/products/order_product_read_options', { per_page: 100 });

          return data.map(item => {
            let label = item.style_number

            if (type === 'new' && item.orders_count === 0) {
              label = `${label} - 首单`
            }

            return { value: item.id, label }
          });
        },
        xRemoteSearch: async (keyword) => {
          const { data } = await this.$request.get('/products/order_product_read_options', { keyword, per_page: 999 });
          return data.map(item => {
            let label = item.style_number

            if (type === 'new' && item.orders_count === 0) {
              label = `${label} - 首单`
            }

            return { value: item.id, label }
          });
        },
      },
      on: {
        change: async (value) => {
          if (type === 'new' && value) {
            data.product = (await this.$request.get('/products/read_options', { id_eq: value })).data[0];

            if (value) {
              await this.fetchProductSalesAndInventory(data.product.id)
            } else {
              this.salesAndInventoryData = {}
            }

            let change_data = { product: data.product, delivery_on: null, line_item_table_attributes: this.setLineItemTableAttributes({ order: data }) }

            if (data.product.return_period) {
              change_data.delivery_on = moment().add(data.product.return_period, 'days').format('YYYY-MM-DD')
            }

            change(change_data)
          }
        }
      },
    }
  }

  salesAndInventoryRenderCell() {
    if (![null, undefined].includes(this.salesAndInventoryData.recent_7_days_sales_count)) {
      return (
        <el-table
          data={[this.salesAndInventoryData]}
        >
          <el-table-column
            prop="fabric_cutting_count"
            label="一条面料裁床数"
          />
          <el-table-column
            prop="sales_level"
            label="销售等级"
          />
          <el-table-column
            prop="inventory_saleable_level"
            label="库存等级"
          />
          <el-table-column
            prop="recent_7_days_sales_count"
            label="近7天销量"
          />
          <el-table-column
            prop="recent_15_days_sales_count"
            label="近15天销量"
          />
          <el-table-column
            prop="current_month_sales_count"
            label="本月销量"
          />
          <el-table-column
            prop="sales_count"
            label="总销量"
          />
          <el-table-column
            prop="inventory_real_available_qty"
            label="当前库存"
          />
          <el-table-column
            prop="inventory_purchase_qty"
            label="采购在途数"
          />
          <el-table-column
            prop="sell_out_rate"
            label="售罄率"
            scopedSlots={{
              default: ({ row }) => {
                if (row.sell_out_rate) {
                  return <span>{`${row.sell_out_rate}%`}</span>
                }
              }
            }}
          />
        </el-table>
      )
    } else {
      return <span>暂无数据</span>
    }
  }

  companyIdRenderCell({ data, change }) {
    return {
      on: {
        change: async (value) => {
          if (value === undefined) {
            return
          }

          data.company_id = value

          if (value) {
            data.company = (await this.$request.get('/companies/read_options', { id_in: [value], page: 0 }))[0];
          } else {
            data.company = null
          }

          change({ company: data.company, company_id: data.company_id, line_item_table_attributes: this.setLineItemTableAttributes({ order: data }) })
        }
      }
    }
  }

  setLineItemTableAttributes({ order }) {
    const abilities = _.get(order, 'abilities', {});
    const product = order.product
    const state = order.state
    const line_items = order.line_items

    if (!product) {
      return []
    }

    const roleKind = _.get(this.$root.user, 'current_role.role.kind');
    const productColorOptionValueIds = _.map(product.color_option_values, (optionValue) => optionValue.id)
    const productSizeOptionValueIds = _.map(product.size_option_values, (optionValue) => optionValue.id)
    let canCreateAndDestroyLineItems = false

    if (roleKind === 'application') {
      canCreateAndDestroyLineItems = !(['wait_supplier_confirm', 'confirmed', 'partial', 'completed'].includes(state))
    } else if (roleKind === 'salesman') {
      canCreateAndDestroyLineItems = abilities.edit_columns || !order.id
    } else if (roleKind === 'sales_director') {
      canCreateAndDestroyLineItems = abilities.edit_columns || !order.id
    } else if (roleKind === 'merchandiser') {
      // 待供应商BOSS确定之后的状态没有编辑权限了
      canCreateAndDestroyLineItems = true
    }

    // 列出product所有Color和Size的组合
    let array = _.filter(
      _.flatten(
        _.map(product.color_option_values, (colorOptionValue) => (
          _.map(product.size_option_values, (sizeOptionValue) => {
            const lineItem = _.find(line_items, (line_item) => {
              return line_item.color_option_value_id === colorOptionValue.id &&
                line_item.size_option_value_id === sizeOptionValue.id
            })
            const variant = _.find(product.variants, (variant) => {
              return variant.color_option_value_id === colorOptionValue.id && variant.size_option_value_id === sizeOptionValue.id
            })

            if (!lineItem && !canCreateAndDestroyLineItems) {
              return null
            } else {
              return {
                sku: variant ? variant.sku : null,
                color: colorOptionValue.name,
                color_option_value_id: colorOptionValue.id,
                size: sizeOptionValue.name,
                size_option_value_id: sizeOptionValue.id,
                quantity: lineItem ? lineItem.quantity : null
              }
            }
          })
        ))
      ), (object) => !!object
    )

    // 列出不在product的Color和Size组合里，但已创建line_item数据的记录（兼容product删除了Color或Size这种场景）
    let existsArray = _.filter(
      _.map(line_items, (lineItem) => {
        if (productColorOptionValueIds.includes(lineItem.color_option_value_id) && productSizeOptionValueIds.includes(lineItem.size_option_value_id)) {
          return null
        } else {
          return {
            sku: lineItem.sku,
            color: lineItem.color_option_value.name,
            color_option_value_id: lineItem.color_option_value_id,
            size: lineItem.size_option_value.name,
            size_option_value_id: lineItem.size_option_value_id,
            quantity: lineItem.quantity
          }
        }
      }), (object) => !!object
    )

    return array.concat(existsArray)
  }

  handleLineItemChange(column, value, data, index, change) {
    let line_item_table_attributes = _.cloneDeep(data.line_item_table_attributes)
    let object = line_item_table_attributes[index]
    let lineItem = _.find(data.line_items, (lineItem) => {
      return lineItem.color_option_value_id === object.color_option_value_id &&
        lineItem.size_option_value_id === object.size_option_value_id
    })

    if (lineItem) {
      lineItem[column] = value
    }

    object[column] = value

    change({ line_item_table_attributes: line_item_table_attributes, line_items: data.line_items })
  }

  get roleKind() {
    return _.get(this.$root.user, 'current_role.role.kind');
  }

  lineItemTableAttributesRenderCell({ row, change }) {
    const abilities = _.get(row, 'abilities', {});
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');
    let disableQuantity = false
    let displaySkuSalesAndInventory = false

    if (['salesman', 'sales_director', 'merchandiser', 'application'].includes(roleKind)) {
      displaySkuSalesAndInventory = true

      _.each(row.line_item_table_attributes, (element) => {
        const salesAndInventoryData = this.findSkuSalesAndInventoryBySkuAndColorAndSize({ sku: element.sku, color: element.color, size: element.size })

        if (salesAndInventoryData) {
          element.inventory_real_available_qty = salesAndInventoryData.inventory_real_available_qty
          element.recent_15_days_sales_count = salesAndInventoryData.recent_15_days_sales_count
          element.recent_30_days_sales_count = salesAndInventoryData.recent_30_days_sales_count
          element.inventory_purchase_qty = salesAndInventoryData.inventory_purchase_qty
          element.labels = salesAndInventoryData.labels
        } else {
          element.inventory_real_available_qty = null
          element.recent_15_days_sales_count = null
          element.recent_30_days_sales_count = null
          element.inventory_purchase_qty = null
          element.labels = []
        }
      })
    }

    if (roleKind === 'merchandiser') {
      disableQuantity = row.id && !['pending', 'admin_rejected', 'boss_rejected'].includes(row.state)
    } else if (roleKind === 'salesman') {
      disableQuantity = row.id && !abilities.salesman_submit
    } else if (roleKind === 'sales_director') {
      disableQuantity = row.id && !['init', 'wait_sales_director_audit', 'sales_director_rejected', 'rejected', 'admin_rejected', 'boss_rejected', 'supplier_boss_rejected', 'supplier_rejected'].includes(row.state)
    }

    return (
      <el-table data={row.line_item_table_attributes} border style="width: 100%">
        {displaySkuSalesAndInventory && <el-table-column label="标签" scopedSlots={{ default: (scope) => (_.map(row.line_item_table_attributes[scope.$index].labels, (object) => <el-tag style={{ 'margin-right': '3px' }}>{object}</el-tag>)) }}  />}
        <el-table-column prop="color" label="颜色" />
        <el-table-column prop="size" label="尺寸" />
        <el-table-column label="数量" scopedSlots={{ default: (scope) => (<el-input disabled={disableQuantity} value={row.line_item_table_attributes[scope.$index].quantity} onInput={value => this.handleLineItemChange('quantity', value, row, scope.$index, change)} />) }} />
        {displaySkuSalesAndInventory && <el-table-column prop="inventory_real_available_qty" label="库存" />}
        {displaySkuSalesAndInventory && <el-table-column prop="recent_15_days_sales_count" label="近15天销量" />}
        {displaySkuSalesAndInventory && <el-table-column prop="recent_30_days_sales_count" label="近30天销量" />}
        {displaySkuSalesAndInventory && <el-table-column prop="inventory_purchase_qty" label="采购在途数" />}
      </el-table>
    )
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type, change }) {
    const abilities = _.get(data, 'abilities', {
      edit_accessory_reach_on: true,
      edit_need_accessory: true,
      edit_columns: true,
      edit_files: true,
    });
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    if (roleKind === 'application') {
      return this.applicationFormColumns({ data, type, change })
    }

    if (['supplier_boss', 'supplier'].includes(roleKind)) {
      return formColumnsExtend([
        abilities.edit_cost && 'fabric_cost',
        abilities.edit_cost && 'assist_cost',
        abilities.edit_cost && 'craft_cost',
        abilities.edit_cost && 'processing_cost',
        abilities.edit_cost && 'management_cost',
        abilities.edit_pre_production_meeting_minutes && 'pre_production_meeting_minutes',
        abilities.edit_pre_production_meeting_minutes && 'pre_production_meeting_images',
        abilities.edit_merchandiser_inspection_remark && 'merchandiser_inspection_remark',
        abilities.edit_merchandiser_inspection_remark && 'merchandiser_inspection_remark_images',
        abilities.edit_factory_id && 'factory_id',
      ].filter(Boolean), { data: data, type: type })
    }

    let canEditDeliveryOn = false
    let canEditCreator = false
    // let canEditVirtualWarehouse = false
    // let disabledVirtualWarehouse = false

    if (roleKind === 'salesman') {
      canEditDeliveryOn = type === 'new' || abilities.salesman_submit
    //  canEditVirtualWarehouse = true
    //  disabledVirtualWarehouse = data.id && !abilities.salesman_submit
    } else if (roleKind === 'sales_director') {
      canEditDeliveryOn = type === 'new' || abilities.edit_columns
    //  canEditVirtualWarehouse = true
    //  disabledVirtualWarehouse = data.id && !abilities.edit_columns
    } else if (roleKind === 'merchandiser') {
      canEditCreator = type === 'new'
      canEditDeliveryOn = type === 'new' || abilities.merchandiser_approve
    //  canEditVirtualWarehouse = true
    }

    const canEditRemark = canEditDeliveryOn

    const array = [
      {
        key: 'product_id',
        renderCell: this.productIdRenderCell({ type, change, data })
      },
      abilities.edit_columns && 'is_pre_order',
      abilities.edit_need_accessory && _.get(data, 'product.product_category.include_accessory') && {
        prop: 'need_accessory',
        label: '需要辅料',
        renderCell: {
          component: 'switch',
        }
      },
      _.get(data, 'product.product_category.include_accessory') && data.need_accessory && {
        key: 'accessory_reach_on',
        renderCell: {
          required: abilities.edit_accessory_reach_on,
          props: {
            disabled: !abilities.edit_accessory_reach_on
          }
        }
      },
      abilityService.can('products.read_sales_and_inventory') && {
        prop: 'sales_and_inventory',
        label: '销量与库存',
        renderCell: () => this.salesAndInventoryRenderCell()
      },
      {
        key: 'delivery_on',
        renderCell: _.merge({ props: { disabled: !canEditDeliveryOn } }, this.deliveryOnRenderCell({ type, data }))
      },
      _.get(data, 'product.product_category.kind') === 'customized' && abilities.edit_columns && {
        key: 'quality_requirement',
        renderCell: {
          required: true,
        }
      },
      _.get(data, 'product.product_category.kind') === 'customized' && abilities.edit_columns && {
        key: 'customer_level',
      },
      canEditCreator && {
        key: 'creator_id',
        renderCell: {
          required: true,
          props: this.usersSelectProps({ item: data.creator, query: { role_eq: 'salesman', is_block_eq: false } })
        }
      },
      {
        key: 'remark',
        renderCell: { props: { disabled: !canEditRemark } }
      },
      this.displayInternalRemark && {
        key: 'internal_remark',
        renderCell: { props: { disabled: !canEditRemark } }
      },
      // canEditVirtualWarehouse && {
      //   key: 'virtual_warehouse_id',
      //   renderCell: { props: { disabled: disabledVirtualWarehouse } }
      // },
      _.get(data, 'product.min_order_qty') && {
        prop: 'min_order_qty',
        label: this.$t('product.minOrderQty'),
        renderCell: (h, { props: { scope: { row } } }) => {
          return <span>{row.product.min_order_qty}</span>
        }
      },
      data.product_id && {
        prop: 'line_item_table_attributes',
        label: this.$t('order.lineItems'),
        renderCell: (h, { props: { scope: { row } } }) => this.lineItemTableAttributesRenderCell({ row, change })
      },
      abilities.edit_files && {
        key: 'files',
        renderCell: {
          props: {
            accept: FILE_ACCEPT,
            type: FILE_TYPE,
            size: 2048,
          }
        }
      }
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  applicationFormColumns({ data, type, change }) {
    const array = [
      {
        key: 'product_id',
        renderCell: this.productIdRenderCell({ type, change, data })
      },
      'is_pre_order',
      _.get(data, 'product.product_category.include_accessory') && {
        prop: 'need_accessory',
        label: '需要辅料',
        renderCell: {
          component: 'switch',
        }
      },
      data.need_accessory && {
        key: 'accessory_reach_on',
        renderCell: {
          required: true
        }
      },
      abilityService.can('products.read_sales_and_inventory') && {
        prop: 'sales_and_inventory',
        label: '销量与库存',
        renderCell: () => this.salesAndInventoryRenderCell()
      },
      {
        key: 'delivery_on',
        renderCell: this.deliveryOnRenderCell({ type, data })
      },
      _.get(data, 'product.product_category.kind') === 'customized' && {
        key: 'quality_requirement',
        renderCell: {
          required: true,
        }
      },
      _.get(data, 'product.product_category.kind') === 'customized' && {
        key: 'customer_level',
      },
      {
        key: 'creator_id',
        renderCell: {
          required: true,
          props: this.usersSelectProps({ item: data.creator, query: { role_eq: 'salesman', is_block_eq: false } })
        }
      },
      // {
      //   key: 'merchandiser_id',
      //   renderCell: {
      //     props: this.usersSelectProps({ item: data.merchandiser, query: { role_eq: 'merchandiser' } })
      //   }
      // },
      !['wait_supplier_boss_confirm', 'wait_supplier_confirm', 'confirmed', 'partial', 'completed'].includes(data.state) && {
        key: 'company_id',
        renderCell: this.companyIdRenderCell({ data, change })
      },
      'remark',
      this.displayInternalRemark && 'internal_remark',
      // 'virtual_warehouse_id',
      _.get(data, 'product.min_order_qty') && {
        prop: 'min_order_qty',
        label: this.$t('product.minOrderQty'),
        renderCell: (h, { props: { scope: { row } } }) => {
          return <span>{row.product.min_order_qty}</span>
        }
      },
      data.product_id && {
        prop: 'line_item_table_attributes',
        label: this.$t('order.lineItems'),
        renderCell: (h, { props: { scope: { row } } }) => this.lineItemTableAttributesRenderCell({ row, change })
      }
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  async handleCreateAccessory(orderId, value) {
    const body = {
      order_id: orderId,
      ...value
    }
    await this.$autoLoading(this.$request.post('/order_accessory_deliveries', body));
    this.$message.success(this.$t('operateSuccess'));
  }

  async handleUpdateAccessory(orderAccessoryDeliveryId, value) {
    const body = {
      ...value
    }
    await this.$autoLoading(this.$request.put(`/order_accessory_deliveries/${orderAccessoryDeliveryId}`, body));
    this.$message.success(this.$t('operateSuccess'));
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { type, remove, reload }) {
    const abilities = _.get(row, 'abilities', {});
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    let canEdit = false
    let editButtonCan = 'orders.merchandiser_update'

    if (roleKind === 'salesman') {
      editButtonCan = []
      canEdit = row.can_update
    } else if (roleKind === 'sales_director') {
      editButtonCan = 'orders.sales_director_update'
      canEdit = row.can_update
    } else if (roleKind === 'merchandiser') {
      canEdit = abilities.merchandiser_update
      editButtonCan = []
    } else if (roleKind === 'application') {
      editButtonCan = []
      canEdit = true
    }

    return [
      canEdit && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          can: editButtonCan,
          to: { name: 'orders.edit', params: { id: row.id } }
        }
      },
      // 目前只支持供应商BOSS
      abilities.update && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          can: [],
          to: { name: 'orders.edit', params: { id: row.id } }
        }
      },
      abilities.create_accessory ? () => {
        const submitHandler = async (value) => {
          await this.handleCreateAccessory(row.id, value);
          reload();
        }
        return <UpdateAccessoryModal lineItems={row.line_items} submitHandler={submitHandler} />
      } : null,

      abilities.salesman_submit && !row.can_sales_director_approve && {
        component: 'confirmButton',
        props: {
          label: '提交审核',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/salesman_submit`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.salesman_withdraw && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/salesman_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.sales_director_approve && {
        component: 'confirmButton',
        props: {
          label: '提交审核',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/sales_director_approve`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.sales_director_withdraw && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/sales_director_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.sales_director_reject && (
        () => <RejectFormDialog data={row} rejectReasons={this.salesDirectorRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/sales_director_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      abilities.merchandiser_approve && {
        component: 'dialogFormButton',
        props: {
          label: '通过',
          columns: formColumnsExtend([
            'remark',
            'internal_remark',
          ], { data: row, type: 'edit' }),
          value: row,
          submitHandler: async (value) =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/merchandiser_approve`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.merchandiser_reject && (
        () => <RejectFormDialog data={row} rejectReasons={this.merchandiserRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/merchandiser_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      abilities.merchandiser_withdraw && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/merchandiser_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.admin_approve && {
        component: 'dialogFormButton',
        props: {
          label: '通过',
          columns: formColumnsExtend([
            {
              key: 'company_id',
              renderCell: {
                required: true,
              }
            },
            // {
            //   key: 'merchandiser_id',
            //   renderCell: {
            //     props: this.usersSelectProps({ item: row.merchandiser, query: { role_eq: 'merchandiser' } })
            //   }
            // }
          ], { data: row, type: 'edit' }),
          value: row,
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/admin_approve`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.admin_reject && (
        () => <RejectFormDialog data={row} rejectReasons={this.adminRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/admin_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      abilities.admin_withdraw && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          type: 'primary'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/admin_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.state === 'wait_boss_audit' && {
        component: 'confirmButton',
        props: {
          label: '同意',
          can: 'orders.boss_approve',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/boss_approve`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilityService.can('orders.boss_reject') && row.state === 'wait_boss_audit' && (
        () => <RejectFormDialog data={row} rejectReasons={this.bossRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/boss_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      row.state === 'wait_supplier_boss_confirm' && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          can: 'orders.boss_withdraw',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/boss_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.supplier_boss_approve && {
        component: 'dialogFormButton',
        props: {
          label: '确认',
          columns: formColumnsExtend([
            {
              key: 'supplier_id',
              renderCell: {
                required: true,
                props: this.usersSelectProps({ item: row.supplier, query: { role_in: ['supplier_boss', 'supplier'], company_id_eq: this.$root.user.user.company_id, is_block_eq: false } })
              }
            },
          ], { data: row, type: 'edit' }),
          value: row,
          submitHandler: async (value) =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/supplier_boss_approve`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.supplier_boss_reject && (
        () => <RejectFormDialog data={row} rejectReasons={this.supplierBossRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/supplier_boss_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          if (type === 'show') {
            this.$router.replace({ name: 'orders.index' });
          } else {
            reload()
          }
        }} />
      ),
      row.state === 'wait_supplier_confirm' && {
        component: 'confirmButton',
        props: {
          label: '撤回',
          can: 'orders.supplier_boss_withdraw',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/supplier_boss_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.update_supplier_remark && {
        component: 'dialogFormButton',
        props: {
          label: '备注',
          columns: formColumnsExtend([
            'supplier_remark',
          ], { data: row, type: 'edit' }),
          value: row,
          submitHandler: async (value) =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/update_supplier_remark`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.state === 'wait_supplier_confirm' && row.supplier_id === this.$root.user.user.id && {
        component: 'confirmButton',
        props: {
          label: '确认',
          can: 'orders.supplier_approve',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/supplier_approve`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilityService.can('orders.supplier_reject') && row.state === 'wait_supplier_confirm' && row.supplier_id === this.$root.user.user.id && (
        () => <RejectFormDialog data={row} rejectReasons={this.supplierBossRejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/supplier_reject`, value));
          this.$message.success(this.$t('operateSuccess'));
            if (type === 'show') {
              this.$router.replace({ name: 'orders.index' });
            } else {
              reload();
            }
        }} />
      ),
      abilities.confirm_withdraw && {
        component: 'confirmButton',
        props: {
          label: '撤回确认',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/confirm_withdraw`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      // 货期修改申请操作
      abilities.submit_delivery_change_request && {
        component: 'dialogFormButton',
        props: {
          label: '申请修改货期',
          columns: formColumnsExtend([
            {
              key: 'requested_delivery_on',
              renderCell: {
                required: true,
              }
            },
            {
              key: 'delivery_change_request_remarks',
            }
          ], { data: {}, type: 'new' }),
          value: {},
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/submit_delivery_change_request`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.withdraw_delivery_change_request && {
        component: 'confirmButton',
        props: {
          label: '撤回货期修改申请',
          type: 'primary'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/withdraw_delivery_change_request`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.approve_delivery_change_request && {
        component: 'dialogFormButton',
        props: {
          label: '批准货期修改',
          columns: formColumnsExtend([
            {
              key: 'delivery_change_request_reject_reason',
            }
          ], { data: row, type: 'edit' }),
          value: {},
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/approve_delivery_change_request`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.reject_delivery_change_request && (
        () => <RejectFormDialog data={row} title='拒绝货期修改' rejectReasons={[]} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/reject_delivery_change_request`, { delivery_change_request_reject_reason: value.reject_reason }));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      // 生产流程操作
      ..._.map([
        { event: 'fabric_arrive', label: '面料到位', columns: ['receive_cloth_remark'] },
        { event: 'printing_start', label: '印绣花进行中', columns: ['printing_remark'] },
        { event: 'workshop_start', label: '车间进行中', columns: ['workshop_remark'] },
        { event: 'tail_start', label: '尾部进行中', columns: ['tail_remark'] },
      ], (hash) => {
        return abilities[hash.event] && {
          component: 'dialogFormButton',
          props: {
            label: hash.label,
            value: row,
            columns: formColumnsExtend(hash.columns.filter(Boolean), { data: row, type: 'edit' }),
            submitHandler: async (value) => {
              await this.$autoLoading(this.$request.put(`/orders/${row.id}/${hash.event}`, value));
              this.$message.success(this.$t('operateSuccess'));
              reload();
            }
          }
        }
      }),
      abilities.push_to_jushuitan && {
        component: 'confirmButton',
        props: {
          label: '推送到聚水潭',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/push_to_jushuitan`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.generate_purchase_order && {
        component: 'confirmButton',
        props: {
          label: '生成采购单',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.post(`/orders/${row.id}/generate_purchase_order`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      // 编辑采购单
      abilities.edit_purchase_order && abilityService.can('orders.push_to_jushuitan') && (
        () => <PurchaseOrderEditModal row={row} onReload={() => reload(row.id)} />
      ),
      abilities.destroy_purchase_order && {
        component: 'confirmButton',
        props: {
          label: '删除采购单',
          type: 'danger',
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.delete(`/orders/${row.id}/destroy_purchase_order`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.generate_manufactures && {
        component: 'dialogFormButton',
        props: {
          label: '生成加工单',
          value: row,
          columns: [
            {
              prop: 'processing_plant_ids',
              label: '加工厂',
              renderCell: {
                component: 'select',
                required: true,
                props: {
                  clearable: true,
                  multiple: true,
                  xRemotePreload: async () => {
                    const { data } = await this.$request.get('/companies/read_options', { per_page: 100 });
                    return data.map(item => ({ value: item.id, label: item.name }));
                  },
                  xRemoteSearch: async (keyword) => {
                    const { data } = await this.$request.get('/companies/read_options', { keyword });
                    return data.map(item => ({ value: item.id, label: item.name }));
                  }
                },
                domProps: {
                  style: 'width: 100%'
                }
              }
            }
          ],
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.post(`/orders/${row.id}/generate_manufactures`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload(row.id);
          }
        }
      },
      // 编辑加工单
      abilities.edit_manufactures && abilityService.can('orders.push_to_jushuitan') && (
        () => <ManufactureEditModal row={row} onReload={() => reload(row.id)} />
      ),
      abilities.destroy_manufactures && {
        component: 'confirmButton',
        props: {
          label: '删除加工单',
          type: 'danger',
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.delete(`/orders/${row.id}/destroy_manufactures`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.watch && {
        component: 'confirmButton',
        props: {
          label: '关注',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/watch`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.unwatch && {
        component: 'confirmButton',
        props: {
          label: '取消关注',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/unwatch`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.deliveries.length && {
        component: 'button',
        props: {
          type: 'primary',
          label: '分享出货单',
          size: 'medium'
        },
        on: {
          click: () => {
            navigator.clipboard.writeText(`${window.location.host}/pages/orders/delivery-list?id=${row.id}`);
            this.$message.success('出货单链接已复制');
          }
        }
      },
      _.some(row.deliveries, object => object.abilities.print) && type === 'index' && {
        component: 'routeButton',
        props: {
          label: '打印出货单',
          to: { name: 'orders.show', params: { id: row.id }, hash: '#2' }
        }
      },
      row.abilities.create_cutting_record && {
        component: 'dialogFormButton',
        props: {
          label: '添加裁床数',
          value: {
            cutting_items_attributes: _.map(row.line_items, (lineItem) => {
              return {
                line_item_id: lineItem.id,
                color: lineItem.color_option_value.name,
                size: lineItem.size_option_value.name,
                order_quantity: lineItem.quantity,
                cutting_quantity: lineItem.cutting_quantity,
                quantity: null
              }
            })
          },
          columns: [{
            label: '',
            prop: 'cutting_items_attributes',
            renderCell: (h, { props: { scope: { row } } }) => {
              return (
                <el-table data={row.cutting_items_attributes} border style="width: 100%">
                  <el-table-column prop="color" label="颜色" />
                  <el-table-column prop="size" label="尺寸" />
                  <el-table-column prop="order_quantity" label="数量" />
                  <el-table-column label="裁床数" scopedSlots={{ default: (scope) => (
                    <el-input
                      placeholder={_.max([0, row.cutting_items_attributes[scope.$index].order_quantity - row.cutting_items_attributes[scope.$index].cutting_quantity])}
                      value={row.cutting_items_attributes[scope.$index].quantity}
                      onInput={value => row.cutting_items_attributes[scope.$index].quantity = value}
                    />
                  ) }} />
                </el-table>
              )
            }
          }],
          submitHandler: async (value) => {
            const cutting_items_attributes = _.filter(value.cutting_items_attributes, (item) => item.quantity > 0)

            await this.$autoLoading(this.$request.post('/cutting_records', { order_id: row.id, cutting_items_attributes: cutting_items_attributes }));
            this.$message.success(this.$t('operateSuccess'));
            reload(row.id);
          }
        }
      },
      row.abilities.create_delivery && {
        component: 'dialogFormButton',
        props: {
          label: '出货',
          value: {
            defective_quantity: null,
            remark: '',
            delivery_items_attributes: _.map(row.line_items, (lineItem) => {
              return {
                line_item_id: lineItem.id,
                color: lineItem.color_option_value.name,
                size: lineItem.size_option_value.name,
                cutting_quantity: lineItem.cutting_quantity,
                order_quantity: lineItem.quantity,
                complete_quantity: lineItem.complete_quantity,
                quantity: null
              }
            })
          },
          columns: deliveryFormColumnsExtend([
            'defective_quantity',
            'remark',
            {
              label: '',
              prop: 'delivery_items_attributes',
              renderCell: (h, { props: { scope: { row } } }) => {
                return (
                  <el-table data={row.delivery_items_attributes} border style="width: 100%">
                    <el-table-column prop="color" label="颜色" />
                    <el-table-column prop="size" label="尺寸" />
                    <el-table-column prop="order_quantity" label="数量" />
                    <el-table-column prop="cutting_quantity" label="裁床数" />
                    <el-table-column label="出货量" scopedSlots={{ default: (scope) => (
                      <el-input
                        placeholder={row.delivery_items_attributes[scope.$index].cutting_quantity - row.delivery_items_attributes[scope.$index].complete_quantity}
                        value={row.delivery_items_attributes[scope.$index].quantity}
                        onInput={value => row.delivery_items_attributes[scope.$index].quantity = value}
                      />
                    ) }} />
                  </el-table>
                )
              }
            }
          ], { data: row, type: 'new' }),
          submitHandler: async (value) => {
            const delivery_items_attributes = _.filter(value.delivery_items_attributes, (item) => item.quantity > 0)

            await this.$autoLoading(this.$request.post('/deliveries', {
              order_id: row.id,
              defective_quantity: value.defective_quantity,
              remark: value.remark,
              delivery_items_attributes: delivery_items_attributes
            }));
            this.$message.success(this.$t('operateSuccess'));
            reload(row.id);
          }
        }
      },
      row.abilities.complete && {
        component: 'confirmButton',
        props: {
          label: '完成订单',
          type: 'primary'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/complete`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.abilities.warning_remind && {
        component: 'confirmButton',
        props: {
          label: '催办',
          type: 'warning'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/orders/${row.id}/warning_remind`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.abilities.back_to_partial && this.aasmConfirmButton({ row, event: 'back_to_partial', label: '回退到部分完成', reload }),
      row.abilities.destroy && {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
        },
        on: {
          confirm: () => remove(row.id)
        }
      },
      this.$refs.sourcePage.type === 'show' && row.files.length > 1 && {
        component: 'button',
        props: {
          label: '下载所有附件',
          type: 'primary',
        },
        on: {
          click: async () => {
            const zip = new JSZip();
            const options = { timeout: 30 * 60 * 1000, responseType: 'blob', return_res: true }
            await Promise.all(row.files.map(async item => {
              const { data } = await this.$request.get(item.url, {}, options);
              zip.file(item.filename, data, { binary: true });
            }));
            const content = await zip.generateAsync({ type: 'blob' });
            this.download(content, `订单-${row.number}-附件.zip`);
            this.$msgbox.alert('下载完成');
          }
        },
      }
    ].filter(Boolean);
  }

  aasmConfirmButton({ row, event, label, reload }) {
    return {
      component: 'confirmButton',
      props: {
        label: label,
        type: 'primary'
      },
      on: {
        confirm: async () => {
          await this.$autoLoading(this.$request.put(`/orders/${row.id}/${event}`));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }
      }
    }
  }

  download(blob, fileName) {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';
    a.href = window.URL.createObjectURL(blob);
    a.download = fileName;
    a.click();
    document.body.removeChild(a);
  }

  async handleSubmit({ data }) {
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    try {
      this.formLoading = true;
      let body = data;
      const action = this.type === 'new' ? 'post' : 'put';
      let path = '/orders'
      if (this.type === 'edit') {
        path = `${path}/${body.id}`

        if (['supplier_boss', 'supplier'].includes(roleKind)) {
          // do nothing
        } else if (roleKind === 'salesman') {
          path = `${path}/salesman_update`
        } else if (roleKind === 'sales_director') {
          path = `${path}/sales_director_update`
        } else if (roleKind === 'application') {
          path = `${path}/application_update`
        } else {
          path = `${path}/merchandiser_update`
        }
      }

      const submitResult = await this.$request[action](path, body);
      const message = this.type === 'new' ? this.$t('bean.createSuccess') : this.$t('bean.updateSuccess');
      this.$message.success(message);
      const id = _.get(submitResult, 'id') || _.get(data, 'id');

      if (id) {
        this.$router.push({ name: 'orders.show', params: { id } });
      }
    } finally {
      this.formLoading = false;
    }
  }

  /**
    * @param {Object} scope -表格 scope 数据
    * @param {Object} scope.selection - 表格已选择行数据
    * @param {Object} sourcePageScope
    * @param {Function} sourcePageScope.remove - sourcePage删除方法
    * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
    */
  batchActions({ selection }, { reload }) {
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');

    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$request.get('/orders/export', { ...this.$route.query });
      } else {
        await this.$request.get('/orders/export', { ...this.$route.query, id_in: _.map(selection, item => item.id) });
      }

      this.$message.success('文件正在导出，导出成功后将会发送到邮箱');
    }

    const canBatchAdminApprove = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.admin_approve', false));
    }

    const canBatchAdminReject = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.admin_reject', false));
    }

    const batchAdminApprove = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要审批的订单');
        return;
      }

      return {
        component: 'dialogFormButton',
        props: {
          label: '批量通过',
          columns: formColumnsExtend([
            {
              key: 'company_id',
              renderCell: {
                required: true,
              }
            },
          ], { data: {}, type: 'edit' }),
          value: {},
          submitHandler: async (value) => {
            const ids = selection.map(row => row.id);
            await this.$autoLoading(
              this.$request.post('/orders/admin_approve', {
                ids: ids,
                company_id: value.company_id
              })
            );
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    }

    const batchAdminReject = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要驳回的订单');
        return;
      }

      return () => <RejectFormDialog
        data={{}}
        title="批量拒绝"
        rejectReasons={this.adminRejectReasons}
        submitHandler={async (value) => {
          const ids = selection.map(row => row.id);
          await this.$autoLoading(
            this.$request.post('/orders/admin_reject', {
              ids: ids,
              reject_reason: value.reject_reason
            })
          );
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }}
      />
    }

    const canBatchSalesmanSubmit = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.salesman_submit', false));
    }

    const canBatchSalesDirectorApprove = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.sales_director_approve', false));
    }

    const canBatchSalesDirectorReject = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.sales_director_reject', false));
    }

    const canBatchMerchandiserApprove = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.merchandiser_approve', false));
    }

    const canBatchMerchandiserReject = () => {
      if (selection.length === 0) return false;
      return selection.every(order => _.get(order, 'abilities.merchandiser_reject', false));
    }

    const batchSalesmanSubmit = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要提交审核的订单');
        return;
      }

      return {
        component: 'confirmButton',
        props: {
          label: '批量提交审核',
          type: 'primary'
        },
        on: {
          confirm: async () => {
            const ids = selection.map(row => row.id);
            await this.$autoLoading(
              this.$request.post('/orders/salesman_submit', {
                ids: ids
              })
            );
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    }

    const batchSalesDirectorApprove = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要提交审核的订单');
        return;
      }

      return {
        component: 'confirmButton',
        props: {
          label: '批量提交审核',
          type: 'primary'
        },
        on: {
          confirm: async () => {
            const ids = selection.map(row => row.id);
            await this.$autoLoading(
              this.$request.post('/orders/sales_director_approve', {
                ids: ids
              })
            );
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    }

    const batchSalesDirectorReject = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要拒绝的订单');
        return;
      }

      return () => <RejectFormDialog
        data={{}}
        title="批量拒绝"
        rejectReasons={this.salesDirectorRejectReasons}
        submitHandler={async (value) => {
          const ids = selection.map(row => row.id);
          await this.$autoLoading(
            this.$request.post('/orders/sales_director_reject', {
              ids: ids,
              reject_reason: value.reject_reason
            })
          );
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }}
      />
    }

    const batchMerchandiserApprove = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要审批的订单');
        return;
      }

      return {
        component: 'dialogFormButton',
        props: {
          label: '批量通过',
          columns: formColumnsExtend([
            'remark',
            'internal_remark',
          ], { data: {}, type: 'edit' }),
          value: {},
          submitHandler: async (value) => {
            const ids = selection.map(row => row.id);
            await this.$autoLoading(
              this.$request.put('/orders/merchandiser_approve', {
                ids: ids,
                ...value
              })
            );
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    }

    const batchMerchandiserReject = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要驳回的订单');
        return;
      }

      return () => <RejectFormDialog
        data={{}}
        title="批量拒绝"
        rejectReasons={this.merchandiserRejectReasons}
        submitHandler={async (value) => {
          const ids = selection.map(row => row.id);
          await this.$autoLoading(
            this.$request.put('/orders/merchandiser_reject', {
              ids: ids,
              reject_reason: value.reject_reason
            })
          );
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }}
      />
    }

    const canBatchUpdate = () => {
      if (selection.length === 0) return false;
      // 暂时只支持管理员批量编辑
      if (roleKind === 'application') {
        return true
      } else {
        return false
      }
    }

    const batchUpdate = () => {
      if (selection.length === 0) {
        this.$message.warning('请选择要编辑的订单');
        return;
      }

      return {
        component: 'dialogFormButton',
        props: {
          label: '批量编辑',
          columns: formColumnsExtend([
            {
              key: 'delivery_on',
              renderCell: {
                required: false,
                hint: '留空则不修改',
              }
            },
            {
              key: 'creator_id',
              renderCell: {
                props: this.usersSelectProps({ query: { role_in: ['salesman', 'sales_director'], is_block_eq: false } }),
                required: false,
                hint: '留空则不修改',
              }
            }
          ], { data: {}, type: 'edit' }),
          value: {},
          submitHandler: async (value) => {
            const ids = selection.map(row => row.id);
            await this.$autoLoading(
              this.$request.put('/orders/update', {
                ids: ids,
                ...value
              })
            );
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    }

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}>导出</el-button>,
      canBatchSalesmanSubmit() && batchSalesmanSubmit(),
      canBatchSalesDirectorApprove() && batchSalesDirectorApprove(),
      canBatchSalesDirectorReject() && batchSalesDirectorReject(),
      canBatchMerchandiserApprove() && batchMerchandiserApprove(),
      canBatchMerchandiserReject() && batchMerchandiserReject(),
      canBatchAdminApprove() && batchAdminApprove(),
      canBatchAdminReject() && batchAdminReject(),
      canBatchUpdate() && batchUpdate(),
    ].filter(Boolean);
  }

  async uploadAccessoryImportFile(order_id, file) {
    const form = new FormData();
    form.append('file_message', file);
    const requestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      return_res: true,
      // 1小时超时
      timeout: 1 * 60 * 60 * 1000
    }
    const result = await this.$request.post(`/orders/${order_id}/order_accessory_deliveries/import`, form, requestConfig);
    if (result.headers['x-error-message']) {
      throw new Error(decodeURIComponent(result.headers['x-error-message']));
    }
  }


   // 为了支持导入成功之后刷新页面
  async handleImprtAccessoryFileChange(e, order_id) {
    const file = e.target.files[0];
    this.importing = true;
    let toastInstance = null;
    toastInstance = this.$message.info({
      message: '导入中...请不要刷新页面',
      duration: 0,
      showClose: true
    });
    try {
      await this.uploadAccessoryImportFile(order_id, file);
      this.$message.success(this.$t('bean.importSuccess'));
      // this.$router.push({ name: 'products.index' });
      this.$refs.sourcePage.fetchData()
    } catch (e) {
      const msg = e.message || this.$t('bean.importFail');
      this.$message.error({
        message: msg,
        showClose: true,
        duration: 0
      });
    } finally {
      this.importing = false;
      if (toastInstance) {
        toastInstance.close();
      }
    }
    e.target.value = '';
  }

  async upload(file) {
    const form = new FormData();
    form.append('file_message', file);
    const requestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      return_res: true,
      // 1小时超时
      timeout: 1 * 60 * 60 * 1000
    }
    const result = await this.$request.post('/orders/import_cost_price', form, requestConfig);
    if (result.headers['x-error-message']) {
      throw new Error(decodeURIComponent(result.headers['x-error-message']));
    }
  }

  async handleImportCostPriceFileChange(e) {
    const file = e.target.files[0];
    this.importing = true;
    let toastInstance = null;
    toastInstance = this.$message.info({
      message: '导入中...请不要刷新页面',
      duration: 0,
      showClose: true
    });
    try {
      await this.upload(file);
      this.$message.success(this.$t('bean.importSuccess'));
      this.$refs.sourcePage.fetchData()
    } catch (e) {
      const msg = e.message || this.$t('bean.importFail');
      this.$message.error({
        message: msg,
        showClose: true,
        duration: 0
      });
    } finally {
      this.importing = false;
      if (toastInstance) {
        toastInstance.close();
      }
    }
    e.target.value = '';
  }
}

export const route = [
  {
    path: '/orders',
    name: 'orders.index',
    component: Orders,
    props: {
      type: 'index',
    }
  },
  {
    path: '/orders/new',
    name: 'orders.new',
    component: Orders,
    props: {
      type: 'new',
    }
  },
  {
    path: '/orders/:id',
    name: 'orders.show',
    component: Orders,
    props: {
      type: 'show',
    }
  },
  {
    path: '/orders/:id/edit',
    name: 'orders.edit',
    component: Orders,
    props: {
      type: 'edit'
    }
  }
]
</script>

<style lang="scss" scoped>
.summary-info {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  p {
    margin: 0;
    font-size: 16px;
    white-space: nowrap;
  }
}
</style>
